import { useId, useRef, useState } from 'react';
import { money, moneyCompact } from '../lib/format';

export interface TrendPoint {
  date: string;
  value: number;
}

// viewBox coordinate space — the SVG scales to its container, these are just its internal units.
const W = 820;
const H = 260;
const PAD = { top: 16, right: 14, bottom: 26, left: 46 };
const innerW = W - PAD.left - PAD.right;
const innerH = H - PAD.top - PAD.bottom;

// Brand tokens (Tailwind classes can't reach SVG gradient stops, so the hexes live here).
const ACCENT = '#E2F26B';
const LINE = '#16170F';
const GRID = '#DCE0D2';
const MUTED = '#7E8475';

/**
 * A single-series area+line chart over time, hand-rolled in SVG so it matches the design system
 * (no chart-lib dependency). Hovering reveals the exact value at each bucket. The parent owns the
 * metric/range toggles; this just draws whatever points it's handed.
 */
export function TrendChart({
  points,
  bucket,
  unit,
  ariaLabel,
}: {
  points: TrendPoint[];
  bucket: 'DAY' | 'WEEK' | 'MONTH';
  unit: 'MONEY' | 'COUNT';
  ariaLabel: string;
}) {
  const gradId = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  const n = points.length;
  const values = points.map((p) => p.value);
  const max = Math.max(1, ...values);
  const baseline = PAD.top + innerH;
  const x = (i: number) => (n <= 1 ? PAD.left + innerW / 2 : PAD.left + (i / (n - 1)) * innerW);
  const y = (v: number) => PAD.top + innerH - (v / max) * innerH;

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const areaPath = n
    ? `M${x(0).toFixed(1)},${baseline} ` +
      points.map((p, i) => `L${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ') +
      ` L${x(n - 1).toFixed(1)},${baseline} Z`
    : '';

  const fmtAxis = (v: number) => (unit === 'MONEY' ? moneyCompact(v) : Math.round(v).toLocaleString());
  const fmtFull = (v: number) => (unit === 'MONEY' ? money(v) : `${Math.round(v).toLocaleString()}`);
  const fmtDate = (iso: string) => {
    const d = new Date(`${iso}T00:00:00`);
    return bucket === 'MONTH'
      ? d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
      : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  // Gridlines at 0 / mid / max.
  const grid = [0, max / 2, max];
  // x-axis ticks: about six, evenly spaced, always including the last bucket.
  const step = Math.max(1, Math.ceil(n / 6));
  const ticks = points.map((_, i) => i).filter((i) => i % step === 0 || i === n - 1);

  function onMove(e: React.PointerEvent<SVGSVGElement>) {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || n === 0) return;
    const ratio = (e.clientX - rect.left) / rect.width;
    const idx = Math.max(0, Math.min(n - 1, Math.round(ratio * (n - 1))));
    setHover(idx);
  }

  const hoverLeftPct = hover != null ? (x(hover) / W) * 100 : 0;
  const hoverAlign = hoverLeftPct < 16 ? '0' : hoverLeftPct > 84 ? '-100%' : '-50%';

  return (
    <div className="relative w-full">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        aria-label={ariaLabel}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={ACCENT} stopOpacity="0.55" />
            <stop offset="100%" stopColor={ACCENT} stopOpacity="0.04" />
          </linearGradient>
        </defs>

        {/* Gridlines + y labels */}
        {grid.map((v, i) => (
          <g key={i}>
            <line x1={PAD.left} y1={y(v)} x2={W - PAD.right} y2={y(v)} stroke={GRID} strokeWidth={1} />
            <text x={PAD.left - 8} y={y(v) + 3.5} textAnchor="end" fontSize={11} fill={MUTED}>
              {fmtAxis(v)}
            </text>
          </g>
        ))}

        {/* Area + line */}
        {n > 0 && <path d={areaPath} fill={`url(#${gradId})`} />}
        {n > 1 && <path d={linePath} fill="none" stroke={LINE} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}

        {/* x labels */}
        {ticks.map((i) => (
          <text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize={11} fill={MUTED}>
            {fmtDate(points[i].date)}
          </text>
        ))}

        {/* Hover guide + dot */}
        {hover != null && (
          <g>
            <line x1={x(hover)} y1={PAD.top} x2={x(hover)} y2={baseline} stroke={LINE} strokeWidth={1} strokeDasharray="3 3" opacity={0.4} />
            <circle cx={x(hover)} cy={y(points[hover].value)} r={4.5} fill={LINE} stroke="#fff" strokeWidth={2} />
          </g>
        )}
      </svg>

      {/* Tooltip */}
      {hover != null && (
        <div
          className="pointer-events-none absolute top-1 z-10 whitespace-nowrap rounded-control bg-dark px-2.5 py-1.5 text-xs text-on-dark shadow-pop"
          style={{ left: `${hoverLeftPct}%`, transform: `translateX(${hoverAlign})` }}
        >
          <span className="font-semibold tabular">{fmtFull(points[hover].value)}</span>
          <span className="ml-1.5 text-on-dark-muted">{fmtDate(points[hover].date)}</span>
        </div>
      )}
    </div>
  );
}
